import { CommonModule } from '@angular/common'
import { Component, OnInit, inject } from '@angular/core'
import { NavigationEnd, Router, RouterOutlet, Scroll } from '@angular/router'
import { catchError, of } from 'rxjs'

import { environment } from '../environments/environment'
import {
  AuthService,
  EventService,
  FlashMessageComponent,
  FooterComponent,
  SideMenuComponent,
  TopMenuComponent,
  TouchMenuComponent,
  ViewportService
} from './abacus'
import { caseConstants } from './abacus/constants/case.constants'
import { MenuItem, MenuSection } from './abacus/interfaces/menu.interface'
import { User } from './abacus/interfaces/resources/user.interface'
import { TopMenuLink } from './abacus/interfaces/top-menu-link.interface'
import { menuItems, menuSections } from './menu-items'
import { IncidentExportService } from './resources/incident/incident-export/incident-export.service'

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    RouterOutlet,
    FlashMessageComponent,
    FooterComponent,
    SideMenuComponent,
    TopMenuComponent,
    TouchMenuComponent
  ],
  host: {
    '(window:resize)': 'onResize()'
  }
})
export class AppComponent implements OnInit {
  private router = inject(Router)
  private eventService = inject(EventService)
  private viewportService = inject(ViewportService)
  private authService = inject(AuthService)
  private incidentExportService = inject(IncidentExportService)

  isLogin = true
  isTouchResolution: boolean
  isCollapsed = false
  path: string
  isStaging: boolean = !environment.production
  menuItems: MenuItem[] = menuItems
  menuSections: MenuSection[] = menuSections

  topMenuLinks: TopMenuLink[] = [
    {
      label: 'Go to tool list',
      icon: 'icon-grid',
      routePath: '/tools',
      permission: 'browseTools'
    }
  ]

  private currentUser: any

  constructor() {
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.eventService.routeChanged.next({ url: event.url })
      } else if (event instanceof Scroll && event.anchor) {
        setTimeout(() => {
          const element: HTMLElement = document.getElementById(event.anchor)
          if (element) {
            element.scrollIntoView()
          }
        }, 100)
      }
    })
  }

  ngOnInit(): void {
    this.authService.currentUser.subscribe((userRes: User) => {
      this.currentUser = userRes
    })

    this.viewportService.isTouchResolution.subscribe((newValue) => {
      this.isTouchResolution = newValue
    })

    this.checkIncidentExportFeature()

    this.setIsTouchResolution()

    this.eventService.routeChanged.subscribe((routeChanged) => {
      // Scroll top
      window.scrollTo(0, 0)

      this.path = routeChanged.url.includes('?')
        ? routeChanged.url.substring(0, routeChanged.url.indexOf('?'))
        : routeChanged.url
      this.isLogin =
        this.path.includes('/login') ||
        this.path.includes('/transparency-reports/register')

      const isLoggedIn = this.authService.isLoggedIn()

      if (!this.isLogin && isLoggedIn && !this.currentUser) {
        this.getCurrentUser()
      }
    })
  }

  onResize(): void {
    this.setIsTouchResolution()
  }

  setIsTouchResolution(): void {
    this.viewportService.isTouchResolution.next(
      window.innerWidth < caseConstants.touchBreakpoint
    )
  }

  getCurrentUser(): void {
    this.authService.me().subscribe((userRes: User) => {
      this.authService.currentUser.next(userRes)

      // Redirect to user's role homepage if on root path and homepagePath is defined
      if (this.path === '/' && userRes?.role?.homepagePath && userRes.role.homepagePath !== '/') {
        this.router.navigate([userRes.role.homepagePath])
      }
    })
  }

  private checkIncidentExportFeature(): void {
    this.incidentExportService
      .getStatus()
      .pipe(catchError(() => of({ enabled: false })))
      .subscribe(({ enabled }) => {
        if (!enabled) {
          this.hideIncidentExportMenu()
        }
      })
  }

  private hideIncidentExportMenu(): void {
    this.menuSections = this.menuSections
      .map((section) => ({
        ...section,
        items: section.items.filter(
          (item) => item.routePath !== '/incidents/export'
        )
      }))
      .filter((section) => section.items.length > 0)
  }
}
